import { z } from "zod";
import httpClient from "@/config/axios";

const shiftSchema = z.object({
  shift_id: z.string(),
  color: z.string().nullable(),
});
export const rosterChangeCellSchema = z.object({
  nurse_id: z.string(),
  nurse_name: z.string().nullable(),
  date: z.string(),
  kind: z.enum(["cell", "added", "removed"]),
  before: shiftSchema.nullable(),
  after: shiftSchema.nullable(),
});
const cellSchema = rosterChangeCellSchema;
const targetSchema = z.object({
  nurse_id: z.string(),
  nurse_name: z.string().nullable(),
  my_changed_cells: z.number(),
  active: z.boolean(),
  acked_at: z.string().nullable(),
  ack_via: z.enum(["app", "web"]).nullable(),
});
const confirmSchema = z.object({
  target_count: z.number(),
  acked_count: z.number(),
});
export const changeNoticeSchema = z.object({
  notice_id: z.number(),
  group_id: z.string(),
  year: z.number(),
  month: z.number(),
  status: z.enum(["open", "closed", "superseded", "withdrawn"]),
  published_at: z.string(),
  total_cells: z.number(),
  my: z.object({
    is_target: z.boolean(),
    my_changed_cells: z.number(),
    acked_at: z.string().nullable(),
  }),
  confirm: confirmSchema,
  superseded_by: z.number().nullable(),
  cells: z.array(cellSchema),
  is_manager: z.boolean().optional(),
  targets: z.array(targetSchema).optional(),
});
export type RosterChangeNotice = z.infer<typeof changeNoticeSchema>;
export type RosterChangeCell = z.infer<typeof cellSchema>;
export type RosterChangeNoticeTarget = z.infer<typeof targetSchema>;
export const changeNoticeKeys = {
  scope: (accountId: string, officeId: string) =>
    ["roster-change-notices", accountId, officeId] as const,
  month: (
    accountId: string,
    officeId: string,
    nurseId: string,
    groupId: string,
    year: number,
    month: number
  ) =>
    [
      ...changeNoticeKeys.scope(accountId, officeId),
      nurseId,
      groupId,
      year,
      month,
    ] as const,
};
export async function fetchRosterChangeNotices(
  groupId: string,
  year: number,
  month: number,
  signal?: AbortSignal
) {
  const result = z
    .object({
      group_id: z.string(),
      year: z.number(),
      month: z.number(),
      is_manager: z.boolean(),
      notices: z.array(changeNoticeSchema),
    })
    .parse(
      await httpClient.get<unknown>("/roster/change-notices", {
        params: { group_id: groupId, year, month },
        signal,
      })
    );
  if (
    result.group_id !== groupId ||
    result.year !== year ||
    result.month !== month ||
    result.notices.some(
      (notice) =>
        notice.group_id !== groupId ||
        notice.year !== year ||
        notice.month !== month
    )
  )
    throw new Error("요청한 병동·기간과 변경 알림이 일치하지 않습니다.");
  return result;
}
export async function fetchRosterChangeNotice(noticeId: number) {
  return changeNoticeSchema.parse(
    await httpClient.get<unknown>(`/roster/change-notices/${noticeId}`)
  );
}
export async function acknowledgeRosterChangeNotice(noticeId: number) {
  return z
    .object({
      notice_id: z.number(),
      status: z.enum(["open", "closed", "superseded", "withdrawn"]),
      acked_at: z.string(),
      confirm: confirmSchema,
    })
    .parse(
      await httpClient.post<unknown>(
        `/roster/change-notices/${noticeId}/ack`,
        undefined,
        { params: { via: "web" } }
      )
    );
}
export function changeCode(code?: string | null): string {
  const value = code?.trim() ?? "";
  return value === "-" ? "" : value;
}
export function changeCellMatches(
  code: string | null | undefined,
  cell: RosterChangeCell
): boolean {
  return changeCode(code) === changeCode(cell.after?.shift_id);
}
export function matchingChangeNotice(
  notices: readonly RosterChangeNotice[],
  snapshotId?: number | null
) {
  return snapshotId == null
    ? undefined
    : notices.find(
        (notice) => notice.status === "open" && notice.notice_id === snapshotId
      );
}
